import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { RiArrowDownSLine } from "react-icons/ri";
import edit from "../../assets/admin/edit.svg";
import deletee from "../../assets/admin/delete.svg";
import Loader from "../../utils/Loader";
import Banner from "../../HomeScreens/Components/Banner";

const CustomerProfile = () => {
  const navigate = useNavigate();
  const { rest_name } = useParams();

  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");

  // Form data state
  const [data, setData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    avatar: null,
  });

  // Form errors state
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    avatar: null,
  });

  // Logo state
  const [selectedLogo, setSelectedLogo] = useState("");
  const [logoError, setLogoError] = useState("");

  // Profile settings data state
  const [profileSettingsData, setProfileSettingsData] = useState([]);

  // Environment variable for logo URL
  const logoUrl = import.meta.env.VITE_BACKEND_APP_STORAGE_URL;

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Modal state
  const [showModalReg, setShowModalReg] = useState(false);

  // Option state
  const [showOption, setShowOption] = useState({ stato: false });

  // Allergens state
  const [allergens, setAllergens] = useState([]);
  const [selectedAllergens, setSelectedAllergens] = useState([]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const toggleDropdown = (dropdownName) => {
    setShowOption({ stato: dropdownName === "stato" ? !showOption.stato : false });
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    if (name === "email" && /\s/.test(value)) return;
    setData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAllergenChange = (e) => {
    const { value, checked } = e.target;
    const allergenId = parseInt(value);
    setSelectedAllergens((prev) =>
      checked ? [...prev, allergenId] : prev.filter((id) => id !== allergenId)
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!data.firstName) newErrors.firstName = "First Name is required";
    if (!data.lastName) newErrors.lastName = "Last Name is required";
    setErrors(newErrors);
    return newErrors;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) return;

    if (logoError) {
      toast.error(logoError, { theme: "colored", autoClose: 1500 });
      return;
    }

    const formData = new FormData();
    formData.append("firstname", data.firstName);
    formData.append("lastname", data.lastName);
    formData.append("email", data.email);
    formData.append("phone", data.phone);

    if (selectedAllergens.length > 0) {
      selectedAllergens.forEach((allergenId) =>
        formData.append("allergens[]", allergenId)
      );
    } else {
      formData.append("allergens", "");
    }
    if (selectedLogo) {
      formData.append("avatar", selectedLogo);
    } else if (data.avatar === null) {
      formData.append("avatar", "");
    }

    try {
      setIsLoading(true);
      const response = await axios.post(`${apiUrl}/api/v1/enduser-profile`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success(response.data?.message, { theme: "colored", autoClose: 1500 });
      getCustomerProfileData();
      navigate(`/restaurant/${rest_name}`);
    } catch (error) {
      console.error("Error saving user data:", error);
      // const errors = error?.response?.data?.errors || {};
      // const errorFields = ["firstname", "lastname", "email", "password"];

      // errorFields.forEach((field) => {
      //   if (errors[field]?.[0]) {
      //     toast.error(errors[field][0], { theme: "colored", autoClose: 1500 });
      //   }
      // });

      // if (!Object.keys(errors).length) {
      //   toast.error("An unexpected error occurred.", { theme: "colored", autoClose: 1500 });
      // }
    } finally {
      setIsLoading(false);
    }
  };

  const getCustomerProfileData = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/v1/enduser`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfileSettingsData(response.data);
    } catch (error) {
      console.error("Error fetching Profile Settings data:", error);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setLogoError("No file selected. Please choose a file to upload.");
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setLogoError("Max size: 2MB. Please upload a smaller file.");
      return;
    }

    const img = new Image();
    img.onload = () => {
      setLogoError("");
      const fileUrl = URL.createObjectURL(file);
      setData((prev) => ({ ...prev, avatar: fileUrl }));
      setSelectedLogo(file);
    };
    img.onerror = () => {
      setLogoError("Invalid image file. Please select a valid image.");
    };
    img.src = URL.createObjectURL(file);
  };

  const deleteProfile = () => {
    setData((prev) => ({ ...prev, avatar: null }))
    setSelectedLogo("");
  }

  useEffect(() => {
    getCustomerProfileData();
  }, []);

  useEffect(() => {
    if (profileSettingsData?.firstname || profileSettingsData?.lastname) {
      setData({
        firstName: profileSettingsData?.firstname || "",
        lastName: profileSettingsData?.lastname || "",
        email: profileSettingsData?.email || "",
        phone: profileSettingsData?.phone || "",
        allergens: profileSettingsData?.status || "",
        avatar: profileSettingsData?.avatar
          ? `${logoUrl}/${profileSettingsData?.avatar}`
          : null,
      });
      const allergenIds = profileSettingsData?.allergens?.map((allergen) => allergen.id) || [];
      setSelectedAllergens(allergenIds);

    }
  }, [profileSettingsData, logoUrl]);

  useEffect(() => {
    const fetchAllergens = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/v1/allergens`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { pageNumber: page, size: rowsPerPage },
        });
        setAllergens(response.data.data);
      } catch (error) {
        console.error("Error fetching allergens", error);
      }
    };
    fetchAllergens();
  }, [page, rowsPerPage]);


  //================================ Out side click drop down ==================================

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowOption({ stato: false });
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  return (
    <div className="flex flex-col w-full h-[100vh] overflow-y-scroll">
      <Banner
        showModalReg={showModalReg}
        setShowModalReg={setShowModalReg}
      />
      <section className="border-[#D9D9D9] md:pt-4 pt-3 sm:px-[15px] px-[15px] max-w-[1230px] mx-auto my-0 w-full mb-4 sm:mb-0">
        <h3 className="md:ext-2xl  text-center text-[28px] leading-[37px] font-bold mb-3 poppins-medium">
          My Profile
        </h3>
        <div className="w-full h-auto bg-[#FCFCFC] text-left text-red-700 space-y-5 border-[2px] border-[#8B24332B] py-3 px-4 sm:px-8 sm:py-5 rounded-xl shadow-md font-sans">
          <div className="flex flex-col space-y-4 mb-4">
            <div className="flex md:flex-row flex-col gap-6">
              <div className="flex flex-col md:w-[50%] w-full space-y-3">
                <label className="sm:text-[18px] text-[16px] leading-[27px] font-semibold poppins-medium text-[#6B6B6B]">
                  First Name :
                </label>
                <input
                  type="text"
                  name="firstName"
                  className="poppins-regular px-4 sm:py-[18px] py-[14px] text-xs text-[#8B8B8B] rounded-[28px] bg-transparent border border-[#777777] focus:outline-none focus:border-[#d5d6d9] focus:bg-[#f6efef]"
                  value={data.firstName?.replace(/[^a-zA-Z\s]/g, "").replace(/\s+/g, " ") || ""}
                  onChange={handleOnChange}
                />
                {errors.firstName && (
                  <div className="text-xs text-red-500 mt-1">{errors.firstName}</div>
                )}
              </div>
              <div className="flex flex-col md:w-[50%] w-full space-y-3">
                <label className="sm:text-[18px] text-[16px] leading-[27px] font-semibold poppins-medium text-[#6B6B6B]">
                  Last Name :
                </label>
                <input
                  type="text"
                  name="lastName"
                  className="poppins-regular px-4 sm:py-[18px] py-[14px] text-xs text-[#8B8B8B] rounded-[28px] bg-transparent border border-[#777777] focus:outline-none focus:border-[#d5d6d9] focus:bg-[#f6efef]"
                  value={data.lastName?.replace(/[^a-zA-Z\s]/g, "").replace(/\s+/g, " ") || ""}
                  onChange={handleOnChange}
                />
                {errors.lastName && (
                  <div className="text-xs text-red-500 mt-1">{errors.lastName}</div>
                )}
              </div>
            </div>
            <div className="flex md:flex-row flex-col gap-6">
              <div className="flex flex-col md:w-[50%] w-full space-y-3">
                <label className="sm:text-[18px] text-[16px] leading-[27px] font-semibold poppins-medium text-[#6B6B6B]">
                  Email :
                </label>
                <input
                  type="text"
                  name="email"
                  className="poppins-regular px-4 sm:py-[18px] py-[14px] text-xs text-[#8B8B8B] rounded-[28px] bg-transparent border border-[#777777] focus:outline-none focus:border-[#d5d6d9] focus:bg-[#f6efef]"
                  value={data.email || ""}
                  onChange={handleOnChange}
                  disabled
                  style={{ cursor: "not-allowed" }}
                />
                {errors.email && (
                  <div className="text-xs text-red-500 mt-1">{errors.email}</div>
                )}
              </div>
              <div className="flex flex-col md:w-[50%] w-full space-y-3">
                <label className="sm:text-[18px] text-[16px] leading-[27px] font-semibold poppins-medium text-[#6B6B6B]">
                  Cellulare :
                </label>
                <input
                  type="number"
                  name="phone"
                  className="poppins-regular px-4 sm:py-[18px] py-[14px] text-xs text-[#8B8B8B] rounded-[28px] bg-transparent border border-[#777777] focus:outline-none  focus:border-[#d5d6d9] focus:bg-[#f6efef]"
                  value={data.phone || ""}
                  onChange={handleOnChange}
                />
                {errors.phone && (
                  <div className="text-xs text-red-500 mt-1">{errors.phone}</div>
                )}
              </div>
            </div>
            <div className="flex md:flex-row flex-col gap-6">
              <div className="flex flex-col md:w-[50%] w-full space-y-3 relative">
                <label className="sm:text-[18px] text-[16px] leading-[27px] font-semibold poppins-medium text-[#6B6B6B]">
                  Profile Picture:
                </label>
                <div className="text-xs text-[#8B8B8B] poppins-regular">
                  Recommended ratio: 800 x 600 px | Max size: 2MB
                </div>
                <div className="relative file-input poppins-regular px-4 py-[18px] text-xs text-[#8B8B8B] rounded-[28px] bg-transparent border border-[#777777] focus:outline-none focus:border-[#d5d6d9] focus:bg-[#f6efef]">
                  {!data?.avatar ? (
                    <>
                      <input
                        className="py-[18px]"
                        type="file"
                        onChange={handleLogoChange}
                        accept="image/*"
                      />
                      <span className="button">Choose</span>
                      <span className="label" data-js-label>
                        No file selected
                      </span>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-between w-full gap-x-2 top-3 right-4 mb-2">
                        <span className="button bg-[#D6D6D6] text-[#6B6B6B] px-[14px] py-[8px] rounded-[28px] inline-block text-xs">Selected file</span>
                        <div className="flex gap-2">
                          <div className="delete bg-[#8b2433] w-[30px] h-[30px] rounded-[50%] flex items-center justify-center p-[7px] cursor-pointer">
                            <img
                              className="invert"
                              src={deletee}
                              height={17}
                              width={17}
                              alt="Delete"
                              onClick={() => deleteProfile()}
                            />
                          </div>
                          <div className="bg-[#8b2433] w-[30px] h-[30px] rounded-full flex items-center justify-center p-[5px] cursor-pointer">
                            <input
                              id="logoInput"
                              type="file"
                              className="hidden"
                              onChange={handleLogoChange}
                              accept="image/*"
                            />
                            <img
                              src={edit}
                              alt="Edit"
                              className="w-[16px] h-[16px] invert"
                              onClick={() => document.getElementById("logoInput").click()}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="overflow-hidden mt-[10px] cursor-pointer h-[200px] w-full">
                        <img
                          src={data?.avatar}
                          alt="Preview"
                          className="w-full h-full object-cover rounded-[11px] shadow-md"
                        />
                      </div>
                    </>
                  )}
                </div>
                {logoError && (
                  <div className="text-xs text-red-500 mt-1">{logoError}</div>
                )}
              </div>
              <div className="flex flex-col md:w-[50%] w-full gap-3">
                <label className="sm:text-[18px] text-[16px] leading-[27px] font-semibold poppins-medium text-[#6B6B6B]">
                  Allergies:
                </label>
                <div className="relative group w-auto bg-[#FFFFFF] dropdown-container">
                  <div className="rounded-3xl py-[3px] bg-transparent border border-gray-300 focus:outline-none focus:border-[#d5d6d9] focus:bg-[#f6efef]">
                    <button
                      className="py-0 items-center w-full relative z-10 poppins-regular px-4 btn-all-yscroll h-[42px] overflow-y-auto flex justify-between text-xs text-[#8B8B8B] rounded-[28px]"
                      onClick={() => toggleDropdown("stato")}
                    >
                      <span className="mr-2 flex items-center gap-3 poppins-medium">
                        {selectedAllergens.length > 0
                          ? allergens
                            .filter((allergen) => selectedAllergens.includes(allergen.id))
                            .map((allergen) => allergen.name)
                            .join(', ')
                          : 'Select Allergies'}
                      </span>
                      <RiArrowDownSLine color="#9f414f" className="text-red-700" size={24} />
                    </button>
                    <div
                      className={`${showOption.stato
                        ? "max-h-60 opacity-100 relative overflow-y-auto"
                        : "max-h-0 opacity-0 overflow-hidden"
                        } bg-[#FFFFFF] absolute top-0 p-2 px-4 mt-0 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 w-full transition-all duration-300 ease-in-out z-[999] btn-all-yscroll`}
                    >
                      {allergens?.map((allergen, index) => (
                        <div className="flex gap-[20px]" key={index}>
                          <label
                            key={allergen.id}
                            className="px-0 py-2 cursor-pointer flex justify-between flex-row gap-[15px] filter_list_btn"
                          >
                            <input
                              type="checkbox"
                              value={allergen.id}
                              checked={selectedAllergens.includes(allergen.id)}
                              onChange={handleAllergenChange}
                            />
                          </label>
                          <span className="text-xs text-[#8B8B8B] rounded-md flex items-center poppins-regular">
                            {allergen?.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex text-sm sm:flex-row flex-col justify-start gap-4 mt-10">
            {isLoading ? (
              <button
                disabled
                type="button"
                className="max-w-[100%] sm:max-w-[200px] w-full h-[54px] poppins-regular justify-center flex items-center space-x-2 p-1 px-3 bg-[#8A2533] text-white border-[1.5px] border-[#d18d97] rounded-[28px] transition-colors group">
                <Loader />
                Loading...
              </button>
            ) : (
              <button
                className="max-w-[100%] sm:max-w-[260px] w-full justify-center flex items-center space-x-2 p-1 px-6 py-4 bg-[#8B2433] border-[1.5px] border-[#d18d97] text-white rounded-[28px] hover:text-gray-50 hover:bg-[#8A2533] transition-colors group"
                onClick={handleSave}
              >
                <span className="poppins-regular">Update Profile</span>
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CustomerProfile;
